import React, { useState, useCallback, useEffect } from "react";
import {
  makeStyles,
  Paper,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Button,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
} from "@material-ui/core";
import { format, parseISO } from "date-fns";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(3) },
  controls: {
    display: "flex",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  paper: { overflowX: "auto" },
  logRow: {
    "&.error td, &.fatal td": { color: theme.palette.error.main },
    "&.warn td": { color: theme.palette.warning.main || "#ff9800" },
  },
}));

const SuperAdminLogs = () => {
  const classes = useStyles();
  const [logs, setLogs] = useState([]);
  const [logCount, setLogCount] = useState(0);
  const [logLevel, setLogLevel] = useState("");
  const [logLoading, setLogLoading] = useState(false);

  const fetchLogs = useCallback(async (level = "", offset = 0) => {
    setLogLoading(true);
    try {
      const params = { limit: 100, offset };
      if (level) params.level = level;
      const { data } = await api.get("/superadmin/logs", { params });
      setLogs(data.logs);
      setLogCount(data.count);
    } catch (err) {
      toastError(err);
    } finally {
      setLogLoading(false);
    }
  }, []);

  useEffect(() => { fetchLogs(logLevel); }, [fetchLogs, logLevel]);

  return (
    <div className={classes.root}>
      <Typography variant="h5" gutterBottom>Logs del sistema</Typography>

      <div className={classes.controls}>
        <Select
          value={logLevel}
          onChange={(e) => setLogLevel(e.target.value)}
          displayEmpty
          style={{ minWidth: 120 }}
        >
          <MenuItem value="">Todos</MenuItem>
          <MenuItem value="error">Error</MenuItem>
          <MenuItem value="fatal">Fatal</MenuItem>
          <MenuItem value="warn">Warn</MenuItem>
          <MenuItem value="info">Info</MenuItem>
        </Select>
        <Button size="small" variant="outlined" onClick={() => fetchLogs(logLevel)}>
          Actualizar
        </Button>
        <Typography variant="caption" color="textSecondary">
          {logCount} registros
        </Typography>
      </div>

      <Paper className={classes.paper}>
        {logLoading ? (
          <CircularProgress style={{ margin: 16 }} />
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nivel</TableCell>
                <TableCell>Mensaje</TableCell>
                <TableCell>Fecha</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="textSecondary">Sin logs</Typography>
                  </TableCell>
                </TableRow>
              )}
              {logs.map((log) => (
                <TableRow key={log.id} className={`${classes.logRow} ${log.level}`}>
                  <TableCell>
                    <Chip
                      label={log.level}
                      size="small"
                      color={log.level === "error" || log.level === "fatal" ? "secondary" : "default"}
                    />
                  </TableCell>
                  <TableCell style={{ maxWidth: 600, wordBreak: "break-word" }}>
                    {log.message}
                  </TableCell>
                  <TableCell>
                    {log.createdAt ? format(parseISO(log.createdAt), "dd/MM/yy HH:mm:ss") : "—"}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>
    </div>
  );
};

export default SuperAdminLogs;
